import { useEffect, useState } from 'react';

const DEFAULT_CALENDAR_HEIGHT = 720;
export const CALENDAR_LAYOUT_SYNC_EVENT = 'app_calendar_layout_sync';

const WIDTH_NODES_SELECTOR =
  '.fc-scrollgrid, .fc-scrollgrid-sync-table, .fc-col-header, .fc-scrollgrid-section table, .fc-scrollgrid-sticky-shim, .fc-scrollgrid-section-header .fc-scroller, .fc-scrollgrid-section-header .fc-scroller-harness, .fc-daygrid-body, .fc-scrollgrid-section-body table, .fc-scrollgrid-section-body .fc-scroller, .fc-scrollgrid-section-body .fc-scroller-harness';

const HEIGHT_NODES_SELECTOR =
  '.fc, .fc-view-harness, .fc-view-harness-active, .fc-scroller, .fc-scroller-harness, .fc-scrollgrid, .fc-scrollgrid-section, .fc-daygrid-body, .fc-daygrid-day, .fc-daygrid-day-frame';

const registeredCalendarRefs = new Set();
let globalFrameId = null;

const setImportantStyle = (node, property, value) => {
  if (!node) return;
  if (value === '' || value == null) {
    node.style.removeProperty(property);
    return;
  }
  node.style.setProperty(property, value, 'important');
};

const clearFullCalendarWidthSync = (rootNode) => {
  if (!rootNode) return;

  rootNode.querySelectorAll(WIDTH_NODES_SELECTOR).forEach((node) => {
    setImportantStyle(node, 'width', '');
    setImportantStyle(node, 'min-width', '');
    setImportantStyle(node, 'max-width', '');
  });

  rootNode.querySelectorAll('.fc-scrollgrid colgroup col, .fc-scrollgrid-section colgroup col').forEach((col) => {
    setImportantStyle(col, 'width', '');
    setImportantStyle(col, 'min-width', '');
  });
};

/** Keep FullCalendar tables flush with the counter-zoomed wrapper width. */
const syncFullCalendarWidthsToWrapper = (calendarComponentRef) => {
  const calendarEl = calendarComponentRef?.current;
  if (!calendarEl?.getApi) return;

  const rootNode = calendarEl.getApi().el;
  if (!rootNode) return;

  if (!isAppZoomBelow100()) {
    clearFullCalendarWidthSync(rootNode);
    return;
  }

  const wrapper = rootNode.parentElement;
  const layoutWidth = wrapper?.offsetWidth || rootNode.offsetWidth;
  if (!layoutWidth) return;

  const widthPx = `${layoutWidth}px`;

  rootNode.querySelectorAll(WIDTH_NODES_SELECTOR).forEach((node) => {
    setImportantStyle(node, 'width', widthPx);
    setImportantStyle(node, 'min-width', widthPx);
    setImportantStyle(node, 'max-width', widthPx);
  });

  rootNode.querySelectorAll('.fc-scrollgrid colgroup, .fc-scrollgrid-section colgroup').forEach((colgroup) => {
    const columnCount = colgroup.children.length;
    if (!columnCount) return;

    const columnWidth = `${layoutWidth / columnCount}px`;
    Array.from(colgroup.children).forEach((col) => {
      setImportantStyle(col, 'width', columnWidth);
      setImportantStyle(col, 'min-width', columnWidth);
    });
  });
};

export const getAppVisualZoomScale = () => {
  const rootElement = document.getElementById('root');
  if (!rootElement) return 1;

  const zoomValue = parseFloat(rootElement.style.zoom);
  if (!Number.isFinite(zoomValue) || zoomValue <= 0) return 1;

  return zoomValue / 100;
};

export const isAppZoomBelow100 = () => getAppVisualZoomScale() < 1;

/** At app zoom below 100%, calendars use auto height so counter-zoom does not clip or scroll. */
export const getCalendarHeightForAppZoom = (baseHeight = DEFAULT_CALENDAR_HEIGHT) => {
  if (isAppZoomBelow100()) return 'auto';
  return baseHeight;
};

const clearFullCalendarHeightSync = (rootNode) => {
  if (!rootNode) return;

  rootNode.querySelectorAll(HEIGHT_NODES_SELECTOR).forEach((node) => {
    setImportantStyle(node, 'height', '');
    setImportantStyle(node, 'min-height', '');
    setImportantStyle(node, 'max-height', '');
    if (node.classList.contains('fc-scroller')) {
      setImportantStyle(node, 'overflow', '');
      setImportantStyle(node, 'overflow-y', '');
      setImportantStyle(node, 'overflow-x', '');
    }
  });

  rootNode.style.height = '';
  rootNode.style.minHeight = '';
  rootNode.style.maxHeight = '';
};

const syncFullCalendarHeightForCounterZoom = (calendarComponentRef) => {
  const calendarEl = calendarComponentRef?.current;
  if (!calendarEl?.getApi) return;

  const calendarApi = calendarEl.getApi();
  const rootNode = calendarApi.el;
  if (!rootNode) return;

  if (!isAppZoomBelow100()) {
    clearFullCalendarHeightSync(rootNode);
    if (calendarApi.getOption('height') === 'auto') {
      calendarApi.setOption('height', DEFAULT_CALENDAR_HEIGHT);
    }
    return;
  }

  if (calendarApi.getOption('height') !== 'auto') {
    calendarApi.setOption('height', 'auto');
  }

  rootNode.querySelectorAll(HEIGHT_NODES_SELECTOR).forEach((node) => {
    setImportantStyle(node, 'height', 'auto');
    setImportantStyle(node, 'min-height', '0');
    setImportantStyle(node, 'max-height', 'none');
    if (node.classList.contains('fc-scroller')) {
      setImportantStyle(node, 'overflow', 'visible');
      setImportantStyle(node, 'overflow-y', 'visible');
      setImportantStyle(node, 'overflow-x', 'hidden');
    }
  });

  setImportantStyle(rootNode, 'height', 'auto');
  setImportantStyle(rootNode, 'min-height', '0');
  setImportantStyle(rootNode, 'max-height', 'none');
};

/** Re-render calendar when app zoom changes so FullCalendar can updateSize. */
export const useCalendarLayoutSync = () => {
  const [layoutSyncKey, setLayoutSyncKey] = useState(0);

  useEffect(() => {
    const bump = () => setLayoutSyncKey((key) => key + 1);

    window.addEventListener('app_zoom_update', bump);
    window.addEventListener(CALENDAR_LAYOUT_SYNC_EVENT, bump);
    window.addEventListener('resize', bump);

    return () => {
      window.removeEventListener('app_zoom_update', bump);
      window.removeEventListener(CALENDAR_LAYOUT_SYNC_EVENT, bump);
      window.removeEventListener('resize', bump);
    };
  }, []);

  return layoutSyncKey;
};

const notifyCalendarLayoutSync = () => {
  window.dispatchEvent(new Event(CALENDAR_LAYOUT_SYNC_EVENT));
};

export const refreshFullCalendarLayout = (calendarComponentRef) => {
  const calendarEl = calendarComponentRef?.current;
  if (!calendarEl?.getApi) return;

  const calendarApi = calendarEl.getApi();

  syncFullCalendarHeightForCounterZoom(calendarComponentRef);
  calendarApi.updateSize();
  syncFullCalendarWidthsToWrapper(calendarComponentRef);

  if (typeof calendarApi.render === 'function') {
    calendarApi.render();
  }

  syncFullCalendarHeightForCounterZoom(calendarComponentRef);
  calendarApi.updateSize();
  syncFullCalendarWidthsToWrapper(calendarComponentRef);
};

export const refreshAllFullCalendarsLayout = () => {
  registeredCalendarRefs.forEach((calendarComponentRef) => {
    refreshFullCalendarLayout(calendarComponentRef);
  });
};

export const scheduleFullCalendarZoomFix = () => {
  if (globalFrameId) cancelAnimationFrame(globalFrameId);
  globalFrameId = requestAnimationFrame(() => {
    refreshAllFullCalendarsLayout();
    notifyCalendarLayoutSync();
    globalFrameId = requestAnimationFrame(() => {
      refreshAllFullCalendarsLayout();
      notifyCalendarLayoutSync();
      globalFrameId = null;
    });
  });
};

export const subscribeFullCalendarZoomRefresh = (calendarComponentRef) => {
  registeredCalendarRefs.add(calendarComponentRef);

  let frameId = null;

  const handleZoomOrResize = () => {
    if (frameId) cancelAnimationFrame(frameId);
    frameId = requestAnimationFrame(() => {
      refreshFullCalendarLayout(calendarComponentRef);
      window.requestAnimationFrame(() => {
        refreshFullCalendarLayout(calendarComponentRef);
      });
      frameId = null;
    });
  };

  window.addEventListener('app_zoom_update', handleZoomOrResize);
  window.addEventListener('resize', handleZoomOrResize);
  handleZoomOrResize();

  return () => {
    registeredCalendarRefs.delete(calendarComponentRef);
    if (frameId) cancelAnimationFrame(frameId);
    window.removeEventListener('app_zoom_update', handleZoomOrResize);
    window.removeEventListener('resize', handleZoomOrResize);
  };
};

export const subscribeGlobalFullCalendarZoomFix = () => {
  const handleZoomOrResize = () => {
    scheduleFullCalendarZoomFix();
  };

  window.addEventListener('app_zoom_update', handleZoomOrResize);
  window.addEventListener('resize', handleZoomOrResize);
  scheduleFullCalendarZoomFix();

  return () => {
    if (globalFrameId) cancelAnimationFrame(globalFrameId);
    window.removeEventListener('app_zoom_update', handleZoomOrResize);
    window.removeEventListener('resize', handleZoomOrResize);
  };
};
